import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Scissors, Sparkles, Info, CheckCircle } from 'lucide-react';

// --- Styled Components ---
const SectionWrapper = styled.section`
  padding: 5rem 0;
  background-color: #fdfdfd;
  color: #1a1a1a;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: #111;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
`;

const TabButton = styled.button`
  padding: 1rem 2.5rem;
  border: 1px solid ${props => props.$active ? '#b8924e' : '#ddd'};
  background: ${props => props.$active ? '#b8924e' : 'white'};
  color: ${props => props.$active ? 'white' : '#555'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 50px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: ${props => props.$active ? '0 4px 15px rgba(184, 146, 78, 0.3)' : 'none'};

  &:hover {
    border-color: #b8924e;
    color: ${props => props.$active ? 'white' : '#b8924e'};
  }
`;

// --- Nail Layout Components ---
const NailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const CategoryCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  border: 1px solid #eee;
  box-shadow: 0 5px 15px rgba(0,0,0,0.02);
`;

const CategoryTitle = styled.h3`
  font-size: 1.5rem;
  color: #b8924e;
  border-bottom: 2px solid #f0e6d2;
  padding-bottom: 0.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PriceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 1rem;
  font-size: 1rem;

  .name {
    flex: 1;
    position: relative;
    overflow: hidden;
    color: #333;

    &::after {
      content: " .....................................................................................................";
      position: absolute;
      padding-left: 5px;
      color: #ccc;
    }
  }

  .price {
    font-weight: 600;
    color: #111;
    padding-left: 10px;
    background: white;
    z-index: 1;
  }
`;

// --- Hair Layout Components ---
const HairContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const HairTable = styled.div`
  width: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #eee;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th {
    background: #1a1a1a;
    color: white;
    padding: 1.2rem;
    text-align: left;
    font-size: 1.1rem;
  }

  td {
    padding: 1.2rem;
    border-bottom: 1px solid #eee;
  }

  tr:last-child td {
    border-bottom: none;
  }

  .service-name {
    font-weight: 600;
    color: #b8924e;
    font-size: 1.1rem;
  }
`;

const NoteBox = styled.div`
  background: #fdf8ef;
  border-left: 4px solid #b8924e;
  padding: 1.5rem;
  margin-top: 2rem;
  border-radius: 0 8px 8px 0;

  p {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #555;
    font-size: 0.95rem;
    margin-bottom: 0.5rem;
  }
`;

// --- Main Component ---
const AboutService = () => {
  const [activeTab, setActiveTab] = useState('nail');
  const [services, setServices] = useState([]);
  const API_URL = "https://nk-salon-hair.germibays.workers.dev/api/services";

  useEffect(() => {
    fetch(API_URL).then(res => res.json()).then(data => setServices(data));
  }, []);

  // Hàm nhóm dữ liệu theo Category
  const getGroupedServices = (type) => {
    const filtered = services.filter(s => s.type === type);
    return filtered.reduce((groups, item) => {
      const group = (groups[item.category] || []);
      group.push(item);
      groups[item.category] = group;
      return groups;
    }, {});
  };

  const nailGroups = getGroupedServices('nail');
  const hairGroups = getGroupedServices('hair');

  return (
    <SectionWrapper id="services">
      <Container>
        <Header>
          <Title>Bảng Giá Dịch Vụ</Title>
          <Subtitle>Chăm sóc sắc đẹp chuyên nghiệp cùng KM Salon</Subtitle>
        </Header>

        <TabContainer>
          <TabButton $active={activeTab === 'nail'} onClick={() => setActiveTab('nail')}>
            <Sparkles size={18} /> Dịch Vụ Nail
          </TabButton>
          <TabButton $active={activeTab === 'hair'} onClick={() => setActiveTab('hair')}>
            <Scissors size={18} /> Dịch Vụ Tóc
          </TabButton>
        </TabContainer>

        {activeTab === 'nail' ? (
          <NailGrid>
            {Object.keys(nailGroups).map(cat => (
              <CategoryCard key={cat}>
                <CategoryTitle>{cat}</CategoryTitle>
                {nailGroups[cat].map(s => (
                  <PriceItem key={s.id}>
                    <span className="name">{s.name}</span>
                    <span className="price">{s.price_s}</span>
                  </PriceItem>
                ))}
              </CategoryCard>
            ))}
          </NailGrid>
        ) : (
          <HairContainer>
            {Object.keys(hairGroups).map(cat => (
              <div key={cat}>
                <CategoryTitle>{cat}</CategoryTitle>
                <HairTable>
                  <table>
                    <thead>
                      <tr><th>Gói dịch vụ</th><th>Size S</th><th>Size M</th></tr>
                    </thead>
                    <tbody>
                      {hairGroups[cat].map(s => (
                        <tr key={s.id}>
                          <td className="service-name">{s.name}</td>
                          <td>{s.price_s}</td>
                          <td>{s.price_m || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </HairTable>
              </div>
            ))}
            <NoteBox>
               <p>📌 Các gói trên đã bao gồm cắt, gội, hấp cơ bản.</p>
               <p>📌 Giá thay đổi tùy thuộc vào độ dài tóc và loại thuốc sử dụng.</p>
            </NoteBox>
          </HairContainer>
        )}
      </Container>
    </SectionWrapper>
  );
};

export default AboutService;